import React, {
    createContext,
    useContext,
    useMemo,
    useState
} from 'react';
import PropTypes from 'prop-types';

/* =========================================================================  */
/* Context(s) */
/* =========================================================================  */

const NavigationContext = createContext({
    address: null,
    backgroundColour: null,
    close: () => {},
    isOpen: [],
    layerWidth: 0,
    links: [],
    open: () => {},
    textColour: null,
    toggle: () => {},
    willClose: false
});

/* =========================================================================  */
/* Hook(s) */
/* =========================================================================  */

/**
 * @hook useNavigation
 */
const useNavigation = () => {
    const context = useContext(NavigationContext);

    if (context === undefined) {
        throw new Error('useNavigation must be used within a NavigationProvider.');
    }

    return context;
};

/* =========================================================================  */
/* Provider(s) */
/* =========================================================================  */

/**
 * @component NavigationProvider
 * @props (see propTypes)
 */
const NavigationProvider = ({
    backgroundColour,
    textColour,
    links,
    children
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [willClose, setWillClose] = useState(false);

    const close = () => {
        setWillClose(true);
        setTimeout(() => {
            setIsOpen(false);
            setWillClose(false);
        }, 0); // Increase to allow for exit transition/s
    };
    const open = () => setIsOpen(true);
    const toggle = () => setIsOpen(!isOpen);

    return (
        <NavigationContext.Provider value={useMemo(() => {
            return {
                backgroundColour,
                close,
                isOpen,
                links,
                open,
                textColour,
                toggle,
                willClose
            };
        }, [
            backgroundColour,
            isOpen,
            links,
            textColour,
            toggle,
            willClose
        ])}>
            {children}
        </NavigationContext.Provider>
    );
};


NavigationProvider.propTypes = {
    backgroundColour: PropTypes.string.isRequired,
    children: PropTypes.any,
    links: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        links: PropTypes.arrayOf(PropTypes.shape({
            title: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        })),
    })),
    textColour: PropTypes.string.isRequired
};

/* =========================================================================  */
/* Export(s) */
/* =========================================================================  */

export {
    NavigationProvider,
    useNavigation
};
